<template>
  <div class="product-page">
    <!-- 页面标题和描述 -->
    <div 
      class="page-header"
      :style="{
        backgroundImage: pageData.cover ? `url(${pageData.cover})` : '',
      }"
    >
      <div class="container">
        <h1>{{ pageData.name || '系统产品' }}</h1>
        <p class="subtitle">{{ pageData.desc }}</p>
      </div>
    </div>

    <!-- 悬浮导航按钮 -->
    <div class="floating-nav">
      <div class="nav-buttons">
        <button 
          class="nav-btn back-to-top"
          @click="scrollToTop"
          :class="{ show: showBackToTop }"
        >
          <el-icon><Top /></el-icon>
          <span>返回顶部</span>
        </button>
        
        <button 
          v-for="system in systems" 
          :key="system.id"
          class="nav-btn"
          :class="{ active: activeSystem === system.id }"
          @click="scrollToSystem(system.id)"
        >
          <el-icon><component :is="system.icon" /></el-icon>
          <span>{{ system.name }}</span>
        </button>
      </div>
    </div>

    <!-- 内容区域 -->
    <div class="content-wrapper">
      <!-- 产品展示区 -->
      <section class="section products-section">
        <div class="container">
          <!-- ITSM: 强调流程管理，使用横向流程展示 -->
          <div id="itsm" class="product-showcase itsm-style">
            <div class="product-intro">
              <div class="product-title">
                <div class="title-bar itsm"></div>
                <div class="title-content">
                  <h2>服务过程管理系统 <span class="product-tag">ITSM</span></h2>
                </div>
              </div>
              <p class="product-desc">ITSM服务过程管理系统是基于大数据技术的企业级服务交付管理平台，旨在帮助企业实现服务流程的数值化、智能化和可视化。通过全流程管控、自动化执行和实时数据分析，帮助服务管理提升服务效率、降低运营成本、增强客户满意度，适用于IT运维服务、售后管理服务、供应链管理等多个服务场景。</p>
            </div>
            
            <div class="process-flow">
              <div class="flow-item">
                <div class="flow-icon"><el-icon><Document /></el-icon></div>
                <h4>标准制定</h4>
                <p>管理制度、工作流程、作业规范</p>
              </div>
              <div class="flow-arrow"><el-icon><ArrowRight /></el-icon></div>
              <div class="flow-item">
                <div class="flow-icon"><el-icon><SetUp /></el-icon></div>
                <h4>系统实施</h4>
                <p>流程植入、系统配置、人员培训</p>
              </div>
              <div class="flow-arrow"><el-icon><ArrowRight /></el-icon></div>
              <div class="flow-item">
                <div class="flow-icon"><el-icon><Monitor /></el-icon></div>
                <h4>过程管理</h4>
                <p>运维管理、过程追溯、结果展示</p>
              </div>
            </div>

            <div class="product-images">
              <div class="monitor-dashboard">
                <div class="dashboard-main">
                  <div class="dashboard-preview">
                    <div class="preview-header">
                      <span class="dot"></span>
                      <span class="dot"></span>
                      <span class="dot"></span>
                    </div>
                    <div class="preview-content">
                      <div class="grid-layout">
                        <div class="grid-item">
                          <h5>工单管理</h5>
                          <div class="chart-placeholder">
                            <img :src="require('@/assets/images/itsm_3.png')" alt="公司照片">                          </div>
                        </div>
                        <div class="grid-item">
                          <h5>坐席管理</h5>
                          <div class="chart-placeholder">
                            <img :src="require('@/assets/images/itsm_2.jpg')" alt="公司照片">                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="product-details">
              <div class="product-features">
                <div class="feature-group">
                  <h3>产品优势</h3>
                  <div class="advantage-cards">
                    <div class="advantage-card">
                      <el-icon><Document /></el-icon>
                      <span>标准规范</span>
                      <p>完整的管理制度与作业规范</p>
                    </div>
                    <div class="advantage-card">
                      <el-icon><SetUp /></el-icon>
                      <span>流程植入</span>
                      <p>标准流程深度植入系统</p>
                    </div>
                    <div class="advantage-card">
                      <el-icon><Monitor /></el-icon>
                      <span>过程管控</span>
                      <p>服务过程可追溯可管理</p>
                    </div>
                    <div class="advantage-card">
                      <el-icon><DataLine /></el-icon>
                      <span>结果展示</span>
                      <p>服务结果量化可视化</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- IDCB: 强调监控大屏效果 -->
          <div id="idcb" class="product-showcase idcb-style">
            <div class="product-intro">
              <div class="product-title">
                <div class="title-bar idcb"></div>
                <div class="title-content">
                  <h2>态势监控平台 <span class="product-tag">IDCB</span></h2>
                </div>
              </div>
              <p class="product-desc">
                IDCB态势监控系统是基于物联网技术的企业级智能监控平台，聚焦于边缘设备的数据实时状态感知与风险预警。通过多维度的数据采集、智能分析和可视化呈现，支持秒级响应与精准决策，适用于IT运维、农业、能源、矿业等多个监控场景。
              </p>
            </div>

            <div class="process-flow">
              <div class="flow-grid">
                <div class="flow-item">
                  <div class="flow-icon"><el-icon><DataLine /></el-icon></div>
                  <h4>全场景数据采集</h4>
                  <p>覆盖IT基础设施（机房/服务器/网络/云资源）、物联网设施（LOT设施/温湿度/酸碱度/能耗率）等多源数据</p>
                  <p>分布式采集，兼容私有云、混合云环境</p>
                </div>
                <div class="flow-item">
                  <div class="flow-icon"><el-icon><Warning /></el-icon></div>
                  <h4>智能分析预警</h4>
                  <p>AI异常检测引擎，自动识别业务波动、性能瓶颈及安全威胁</p>
                  <p>动态阈值算法，消除传统静态报警误报率高的痛点</p>
                  <p>多渠道预警通知（邮件/短信/企业微信/电话），支持自定义响应策略</p>
                </div>
                <div class="flow-item">
                  <div class="flow-icon"><el-icon><Search /></el-icon></div>
                  <h4>可视化态势看板</h4>
                  <p>3D数字孪生技术，实时渲染业务运行全景</p>
                  <p>自定义多维度看板（健康度/风险等级/资源利用率）</p>
                  <p>实时查看与应急指挥，保障 7×24 小时业务连续性</p>
                </div>
                <div class="flow-item">
                  <div class="flow-icon"><el-icon><Monitor /></el-icon></div>
                  <h4>风险溯源与决策支持</h4>
                  <p>根因分析（RCA）自动化定位故障源头</p>
                  <p>业务影响评估模型，量化故障对用户、收入的潜在损失</p>
                  <p>历史数据回溯与趋势预测，提供中长期容量规划建议</p>
                </div>
              </div>
            </div>

            <div class="product-images">
              <div class="monitor-dashboard">
                <div class="dashboard-main">
                  <div class="dashboard-preview">
                    <div class="preview-header">
                      <span class="dot"></span>
                      <span class="dot"></span>
                      <span class="dot"></span>
                    </div>
                    <div class="preview-content">
                      <div class="grid-layout">
                        <div class="grid-item">
                          <h5>网络监控</h5>
                          <div class="chart-placeholder">
                            <img :src="require('@/assets/images/idcb_1.png')" alt="网络监控">                          </div>
                        </div>
                        <div class="grid-item">
                          <h5>主机监控</h5>
                          <div class="chart-placeholder">
                            <img :src="require('@/assets/images/idcb_2.png')" alt="主机监控">                          </div>
                        </div>
                        <div class="grid-item">
                          <h5>动环监控</h5>
                          <div class="chart-placeholder">
                            <img :src="require('@/assets/images/idcb_3.png')" alt="动环监控">                          </div>
                        </div>
                        <div class="grid-item">
                          <h5>预警管理</h5>
                          <div class="chart-placeholder">
                            <img :src="require('@/assets/images/idcb_4.png')" alt="预警管理">                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="product-details">
              <div class="product-features">
                <div class="feature-group">
                  <h3>产品优势</h3>
                  <div class="advantage-cards">
                    <div class="advantage-card">
                      <el-icon><DataLine /></el-icon>
                      <span>智能采集</span>
                      <p>多源数据采集，兼容多云环境</p>
                    </div>
                    <div class="advantage-card">
                      <el-icon><Warning /></el-icon>
                      <span>精准预警</span>
                      <p>AI检测引擎，动态阈值算法</p>
                    </div>
                    <div class="advantage-card">
                      <el-icon><Monitor /></el-icon>
                      <span>实时监控</span>
                      <p>3D数字孪生，多维度展示</p>
                    </div>
                    <div class="advantage-card">
                      <el-icon><Search /></el-icon>
                      <span>智能分析</span>
                      <p>根因分析，趋势预测</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- TCMS: 强调培训认证流程 -->
          <div id="tcms" class="product-showcase tcms-style">
            <div class="product-intro">
              <div class="product-title">
                <div class="title-bar tcms"></div>
                <div class="title-content">
                  <h2>培训认证管理系统 <span class="product-tag">TCMS</span></h2>
                </div>
              </div>
              <p class="product-desc">TCMS培训认证系统是基于课程管理、学员培养、考试认证于一体的企业级内训平台，旨在帮助企业构建标准化、数字化、可追溯的人才培养体系。通过个性化的学习路径、证书防篡改技术和全流程数据监控，系统助力企业提升培训效率、保障认证权威性，适用于员工技能提升、合作伙伴认证、行业资质培训等多个场景。</p>
            </div>
            
            <div class="training-process">
              <div class="flow-grid">
                <div class="flow-item">
                  <div class="flow-icon"><el-icon><Reading /></el-icon></div>
                  <h4>智能课程管理</h4>
                  <p>多格式课程库（视频/文档）</p>
                  <p>AI课程推荐引擎，基于岗位需求与学员画像智能匹配学习内容</p>
                  <p>灵活的课程权限管理，支持按部门/角色分层授权</p>
                </div>
                <div class="flow-item">
                  <div class="flow-icon"><el-icon><School /></el-icon></div>
                  <h4>学员成长追踪</h4>
                  <p>学习进度实时监控，支持碎片化学习</p>
                  <p>智能错题本与薄弱环节分析，个性化学习计划动态调整</p>
                  <p>学习行为数据看板（活跃度/完成率/考试成绩）</p>
                </div>
                <div class="flow-item">
                  <div class="flow-icon"><el-icon><Medal /></el-icon></div>
                  <h4>智能考试认证</h4>
                  <p>在线考试系统，支持随机组卷、防作弊监测</p>
                  <p>证书存证，确保认证不可篡改</p>
                  <p>自动生成认证报告，支持与HR系统无缝对接</p>
                </div>
                <div class="flow-item">
                  <div class="flow-icon"><el-icon><Medal /></el-icon></div>
                  <h4>数据化决策支持</h4>
                  <p>培训 ROI 分析，量化课程投入与员工绩效提升关联度</p>
                  <p>人才能力矩阵评估，精准定位团队技能短板</p>
                  <p>自定义报表与可视化看板，支持多维度数据分析</p>
                </div>
              </div>
            </div>

            <div class="product-images">
              <div class="monitor-dashboard">
                <div class="dashboard-main">
                  <div class="dashboard-preview">
                    <div class="preview-header">
                      <span class="dot"></span>
                      <span class="dot"></span>
                      <span class="dot"></span>
                    </div>
                    <div class="preview-content">
                      <div class="grid-layout">
                        <div class="grid-item">
                          <h5>培训管理</h5>
                          <div class="chart-placeholder">
                            <img :src="require('@/assets/images/tcms_1.png')" alt="公司照片">                          </div>
                        </div>
                        <div class="grid-item">
                          <h5>随堂测验</h5>
                          <div class="chart-placeholder">
                            <img :src="require('@/assets/images/tcms_3.png')" alt="公司照片">                          </div>
                        </div>
                     
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="product-details">
              <div class="product-features">
                <div class="feature-group">
                  <h3>产品优势</h3>
                  <div class="advantage-cards">
                    <div class="advantage-card">
                      <el-icon><School /></el-icon>
                      <span>专业培训</span>
                    </div>
                    <div class="advantage-card">
                      <el-icon><Medal /></el-icon>
                      <span>标准认证</span>
                    </div>
                    <div class="advantage-card">
                      <el-icon><Reading /></el-icon>
                      <span>在线学习</span>
                    </div>
                    <div class="advantage-card">
                      <el-icon><Histogram /></el-icon>
                      <span>实时考核</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- MeinnDM 数据治理系统板块 -->
          <div id="meinnDM" class="product-showcase meinnDM-style">
            <div class="product-intro">
              <div class="product-title">
                <div class="title-bar meinnDM"></div>
                <div class="title-content">
                  <h2>数据治理系统 <span class="product-tag">MeinnDM</span></h2>
                </div>
              </div>
              <p class="product-desc">
                MeinnDM数据治理系统是基于AI和大数据技术的企业级智能平台，致力于帮助客户构建"数据采集—清洗—存储—应用"的全链路治理体系。通过自动化工具、智能分析和可视化决策支持，系统实现数据资产的高效管理、质量提升与合规保障，助力企业释放数据价值，驱动业务创新。
              </p>
            </div>

            <div class="data-governance-grid">

              <!-- 数据质量管理 -->
              <div class="feature-card">
                <div class="card-header">
                  <el-icon><Check /></el-icon>
                  <h3>数据质量管理</h3>
                </div>
                <ul class="feature-list">
                  <li>自动化数据校验规则引擎（唯一性/完整性/一致性）</li>
                  <li>数据质量评分看板，实时监控数据健康度</li>
                  <li>数据清洗任务编排，支持批量修复与异常数据隔离</li>
                </ul>
              </div>
           

              <!-- 智能元数据管理 -->
              <div class="feature-card">
                <div class="card-header">
                  <el-icon><Connection /></el-icon>
                  <h3>智能元数据管理</h3>
                </div>
                <ul class="feature-list">
                  <li>自动采集多源系统元数据（数据库/文件/API）</li>
                  <li>血缘分析与影响分析，支持变更影响评估与风险预警</li>
                </ul>
              </div>


              <!-- 主数据管理 -->
              <div class="feature-card">
                <div class="card-header">
                  <el-icon><Management /></el-icon>
                  <h3>主数据管理（MDM）</h3>
                </div>
                <ul class="feature-list">
                  <li>多域主数据（客户/产品/供应商）集中管理</li>
                  <li>主数据标准制定与版本控制，确保跨系统数据一致性</li>
                </ul>
              </div>
 <!-- 数据标准化（或数据标准管理） -->
 <div class="feature-card">
                <div class="card-header">
                  <el-icon><Lock /></el-icon>
                  <h3>数据标准化（或数据标准管理）</h3>
                </div>
                <ul class="feature-list">
                  <li>统一数据命名规范、数据单位、数据格式。                  </li>
                  <li>通过标准与实际数据比对，快速定位问题数据。                  </li>
                  <li>确保数据的一致性和可比性）</li>
                </ul>
              </div>
              <!-- 数据安全与合规 -->
              <div class="feature-card">
                <div class="card-header">
                  <el-icon><Lock /></el-icon>
                  <h3>数据安全与合规</h3>
                </div>
                <ul class="feature-list">
                  <li>敏感数据自动识别与分级分类</li>
                  <li>细粒度权限管控与审计追踪，支持 GDPR/等保 2.0 合规</li>
                  <li>数据生命周期管理（销毁/归档策略）</li>
                </ul>
              </div>

             

                 <!-- 数据治理门户 -->
                 <div class="feature-card">
                <div class="card-header">
                  <el-icon><Document /></el-icon>
                  <h3>数据治理门户</h3>
                </div>
                <ul class="feature-list">
                  <li>统一数据资产目录，支持业务术语与技术元数据自动关联</li>
                  <li>智能搜索与标签系统，实现数据快速定位与权限管控</li>
                </ul>
              </div>
            </div>

            <div class="product-images">
              <!-- <div class="monitor-dashboard">
                <div class="dashboard-main">
                  <div class="dashboard-preview">
                    <div class="preview-header">
                      <span class="dot"></span>
                      <span class="dot"></span>
                      <span class="dot"></span>
                    </div>
                    <div class="preview-content">
                      <div class="grid-layout">
                        <div class="grid-item">
                          <h5>数据门户</h5>
                          <div class="chart-placeholder">
                            <img :src="require('@/assets/images/gs.jpg')" alt="公司照片">                          </div>
                        </div>
                        <div class="grid-item">
                          <h5>元数据管理</h5>
                          <div class="chart-placeholder">
                            <img :src="require('@/assets/images/gs.jpg')" alt="公司照片">                          </div>
                        </div>
                        <div class="grid-item">
                          <h5>质量管理</h5>
                          <div class="chart-placeholder">
                            <img :src="require('@/assets/images/gs.jpg')" alt="公司照片">                          </div>
                        </div>
                        <div class="grid-item">
                          <h5>安全合规</h5>
                          <div class="chart-placeholder">
                            <img :src="require('@/assets/images/gs.jpg')" alt="公司照片">                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div> -->
            </div>

            <div class="product-details">
              <div class="product-features">
                <div class="feature-group">
                  <h3>产品优势</h3>
                  <div class="advantage-cards">
                    <div class="advantage-card">
                      <el-icon><Document /></el-icon>
                      <span>智能治理</span>
                      <p>AI驱动，自动化数据治理</p>
                    </div>
                    <div class="advantage-card">
                      <el-icon><Check /></el-icon>
                      <span>质量保障</span>
                      <p>全方位数据质量管理</p>
                    </div>
                    <div class="advantage-card">
                      <el-icon><Lock /></el-icon>
                      <span>安全合规</span>
                      <p>数据安全与隐私保护</p>
                    </div>
                    <div class="advantage-card">
                      <el-icon><Management /></el-icon>
                      <span>价值驱动</span>
                      <p>数据资产价值最大化</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { columnGet2 } from "@/api/court";
import { 
  Check, Search, DataLine, Monitor,
  Share, Connection, Setting, Timer,
  School, Medal, Reading, Histogram,
  Document, ArrowRight, SetUp, Top,
  Management, Lock, Warning, User
} from '@element-plus/icons-vue'

const pageData = ref({})

// 获取页面数据
const getPageData = async () => {
  try {
    const response = await columnGet2({
      page: 1,
      limit: 10000,
      is_enable: true,
      parent: 9,
    });
    if (response.data.code === 2000) {
      // 根据当前页面名称匹配对应的数据
      const data = response.data.data.data.find(item => {
        // 根据路径匹配对应的数据
        return item.name.includes('系统产品') || item.name.toLowerCase().includes('system')
      })
      if (data) {
        pageData.value = data
      }
    }
  } catch (error) {
    console.error('获取页面数据失败:', error)
  }
}

onMounted(() => {
  getPageData()
})

// 系统数据
const systems = [
  { id: 'itsm', name: '服务过程管理', icon: 'Monitor' },
  { id: 'idcb', name: '态势监控平台', icon: 'DataLine' },
  { id: 'tcms', name: 'TCMS培训认证', icon: 'School' },
  { id: 'meinnDM', name: '数据治理系统', icon: 'Management' }
];

const activeSystem = ref('')
const showBackToTop = ref(false)

// 滚动处理
function handleScroll() {
  showBackToTop.value = window.scrollY > 300
  
  // 更新当前激活的系统
  const scrollPosition = window.scrollY + window.innerHeight / 3
  for (let i = systems.length - 1; i >= 0; i--) {
    const element = document.getElementById(systems[i].id)
    if (element && element.offsetTop <= scrollPosition) {
      activeSystem.value = systems[i].id
      break
    }
  }
}

// 滚动到指定系统
function scrollToSystem(id) {
  const element = document.getElementById(id)
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' })
  }
}

function scrollToTop() {
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

onMounted(() => {
  // 确保页面从顶部开始显示
  document.documentElement.scrollTop = 0
  document.body.scrollTop = 0
  window.scrollTo(0, 0)

  window.addEventListener('scroll', handleScroll)
  handleScroll()
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})
</script>

<style lang="scss" scoped>
// 定义主题色变量
$primary-color: #2b5dff;
$primary-light: rgba(43, 93, 255, 0.1);
$border-color: #e2e8f0;

.product-page {
  padding-top: 30px;
}

.page-header {
  background-size: cover;
  color: #fff;
  padding: 60px 0 60px;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background: rgba(0, 0, 0, 0.5);
    opacity: 0.5;
  }

  .container {
    position: relative;
    z-index: 1;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
  }

  h1 {
    font-size: 36px;
    margin-bottom: 10px;
    position: relative;
    display: inline-block;
    opacity: 0;
    transform: translateY(20px);
    animation: fadeInUp 0.8s ease forwards;
  }

  .subtitle {
    font-size: 18px;
    opacity: 0;
    transform: translateY(20px);
    animation: fadeInUp 0.8s ease forwards 0.2s;
    margin: 10px auto 0;
    max-width: 800px;
    line-height: 1.8;
  }
}

.products-section {
  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
  }

  .product-showcase {
    margin-bottom: 80px;
    padding: 40px;
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
    transition: all 0.3s ease;
    scroll-margin-top: 80px;
    
    &:hover {
      box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
      transform: translateY(-2px);
    }

    .product-tag { 
      background: $primary-light !important; 
      color: $primary-color !important; 
    }
    .title-bar { background: $primary-color !important; }

    .product-intro {
      margin-bottom: 40px;
      
      .product-title {
        display: flex;
        align-items: center;
        gap: 16px;
        margin-bottom: 24px;
        position: relative;

        .title-bar {
          width: 3px;
          height: 24px;
          background: var(--el-color-primary);
          border-radius: 1.5px;
        }

        .title-content {
          flex: 1;
          display: flex;
          align-items: center;

          h2 {
            font-size: 24px;
            color: var(--el-text-color-primary);
            margin: 0;
            font-weight: 600;
            display: flex;
            align-items: center;
            gap: 12px;

            .product-tag {
              display: inline-flex;
              align-items: center;
              padding: 4px 12px;
              background: var(--el-color-primary-light-9);
              color: var(--el-color-primary);
              border-radius: 4px;
              font-size: 14px;
              font-weight: 500;
              margin: 0;
              font-weight: 600;
              letter-spacing: 0.5px;
            }
          }
        }
      }

      .product-desc {
        font-size: 15px;
        line-height: 1.8;
        color: var(--el-text-color-regular);
        max-width: 900px;
        padding-left: 19px;
      }
    }

    .product-images {
      margin: 40px 0;
      
      .image-container {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 30px;
        
        .image-wrapper {
          background: #fff;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
          
          &:hover {
            transform: translateY(-5px);
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
          }
          
          .image-placeholder {
            width: 100%;
            height: 300px;
            background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
            display: flex;
            align-items: center;
            justify-content: center;
            color: #64748b;
            font-size: 16px;
            border-radius: 12px 12px 0 0;
            border: 1px dashed #e2e8f0;
            
            span {
              font-weight: 500;
            }
          }
          
          .image-caption {
            padding: 12px 16px;
            font-size: 14px;
            color: #666;
            text-align: center;
            background: #fff;
            border-top: 1px solid #eee;
          }
        }
      }
    }

    .product-details {
      display: flex;
      gap: 60px;
      align-items: flex-start;
      padding: 30px;
      background: linear-gradient(135deg, #f8fafc 0%, #fff 100%);
      border-radius: 12px;
      border: 1px solid #e2e8f0;

      &.reverse {
        flex-direction: row-reverse;
      }

      .product-features {
        flex: 1;

        .feature-group {
          margin-bottom: 30px;

          &:last-child {
            margin-bottom: 0;
          }

          h3 {
            font-size: 18px;
            color: var(--el-text-color-primary);
            margin-bottom: 16px;
            position: relative;
            padding-left: 12px;
            font-weight: 600;

            &::before {
              content: '';
              position: absolute;
              left: 0;
              top: 50%;
              transform: translateY(-50%);
              width: 3px;
              height: 16px;
              background: currentColor;
              border-radius: 1.5px;
            }
          }
        }

        .feature-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;

          span {
            padding: 8px 16px;
            background: #fff;
            border-radius: 4px;
            font-size: 14px;
            color: var(--el-text-color-regular);
            transition: all 0.3s ease;
            border: 1px solid #e2e8f0;
            font-weight: 500;
            
            &:hover {
              background: $primary-light;
              border-color: $primary-color;
              color: $primary-color;
            }
          }
        }

        .advantage-cards {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
          gap: 16px;

          .advantage-card {
            background: #fff;
            padding: 20px;
            border-radius: 8px;
            text-align: center;
            transition: all 0.3s ease;
            border: 1px solid #e2e8f0;

            &:hover {
              background: $primary-light;
              border-color: $primary-color;
              
              .el-icon {
                color: $primary-color;
              }
              
              span {
                color: $primary-color;
              }
            }

            .el-icon {
              font-size: 24px;
              color: var(--el-text-color-secondary);
              margin-bottom: 12px;
              transition: all 0.3s ease;
            }

            span {
              display: block;
              font-size: 14px;
              color: var(--el-text-color-regular);
            }
          }
        }
      }
    }
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media screen and (max-width: 768px) {
  .page-header {
    padding: 60px 0 30px;
    margin-bottom: 40px;

    h1 {
      font-size: 28px;
    }

    .subtitle {
      font-size: 16px;
    }

    .description {
      font-size: 14px;
    }
  }

  .products-section {
    .product-showcase {
      padding: 20px;
      scroll-margin-top: 60px;

      .product-images {
        .image-container {
          grid-template-columns: 1fr;
          gap: 20px;
          
          .image-wrapper {
            .image-placeholder {
              height: 200px;
            }
          }
        }
      }

      .product-details {
        flex-direction: column !important;
        gap: 30px;
        padding: 20px;

        .product-features {
          flex: 1;
        }
      }
    }
  }
}

// ITSM 特色样式
.itsm-style {
  .process-flow {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 30px;
    margin: 40px 0;
    padding: 40px;
    background: linear-gradient(135deg, #f8fafc 0%, #fff 100%);
    border-radius: 16px;
    border: 1px solid #e2e8f0;

    .flow-item {
      flex: 1;
      text-align: center;
      padding: 30px;
      background: #fff;
      border-radius: 12px;
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
      transition: all 0.3s ease;

      &:hover {
        transform: translateY(-5px);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
      }

      .flow-icon {
        width: 60px;
        height: 60px;
        margin: 0 auto 20px;
        background: rgba(44, 62, 80, 0.1);
        border-radius: 30px;
        display: flex;
        align-items: center;
        justify-content: center;

        .el-icon {
          font-size: 24px;
          color: #2c3e50;
        }
      }

      h4 {
        font-size: 18px;
        margin-bottom: 10px;
        color: #333;
      }

      p {
        font-size: 14px;
        color: #666;
        line-height: 1.6;
      }
    }

    .flow-arrow {
      color: #2c3e50;
      font-size: 24px;
    }
  }
}

// IDCB 特色样式
.idcb-style {
  .process-flow {
    margin: 30px 0;
    padding: 30px;
    background: linear-gradient(135deg, #f8fafc 0%, #fff 100%);
    border-radius: 16px;
    border: 1px solid #e2e8f0;

    .flow-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 20px;
    }

    .flow-item {
      padding: 20px;
      background: #fff;
      border-radius: 12px;
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
      transition: all 0.3s ease;

      &:hover {
        transform: translateY(-5px);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
      }

      .flow-icon {
        width: 50px;
        height: 50px;
        margin: 0 auto 15px;
        background: rgba(44, 62, 80, 0.1);
        border-radius: 25px;
        display: flex;
        align-items: center;
        justify-content: center;

        .el-icon {
          font-size: 20px;
          color: #2c3e50;
        }
      }

      h4 {
        font-size: 16px;
        margin-bottom: 12px;
        color: #333;
        text-align: center;
      }

      p {
        font-size: 13px;
        color: #666;
        line-height: 1.5;
        text-align: center;
        margin-bottom: 8px;
        padding: 0 10px;

        &:last-child {
          margin-bottom: 0;
        }
      }
    }
  }

  .idcb-image {
    .image-wrapper.full-width {
      width: 100%;
      
      .image-placeholder {
        height: 400px;
        background: linear-gradient(135deg, #1a365d 0%, #2d3748 100%);
        border-radius: 12px;
        overflow: hidden;
        transition: all 0.3s ease;
        
        &:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
        }
      }
      
      .image-caption {
        text-align: center;
        margin-top: 15px;
        font-size: 16px;
        color: #2d3748;
      }
    }
  }
}

// TCMS 特色样式优化
.tcms-style {
  .training-process {
    margin: 30px 0;
    padding: 30px;
    background: linear-gradient(135deg, #f8fafc 0%, #fff 100%);
    border-radius: 16px;
    border: 1px solid #e2e8f0;

    .flow-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 20px;
    }

    .flow-item {
      padding: 20px;
      background: #fff;
      border-radius: 12px;
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
      transition: all 0.3s ease;

      &:hover {
        transform: translateY(-5px);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
      }

      .flow-icon {
        width: 50px;
        height: 50px;
        margin: 0 auto 15px;
        background: rgba(44, 62, 80, 0.1);
        border-radius: 25px;
        display: flex;
        align-items: center;
        justify-content: center;

        .el-icon {
          font-size: 20px;
          color: #2c3e50;
        }
      }

      h4 {
        font-size: 16px;
        margin-bottom: 12px;
        color: #333;
        text-align: center;
      }

      p {
        font-size: 13px;
        color: #666;
        line-height: 1.5;
        text-align: center;
        margin-bottom: 8px;
        padding: 0 10px;

        &:last-child {
          margin-bottom: 0;
        }
      }
    }
  }
}

// 添加悬浮导航按钮样式
.floating-nav {
  position: fixed;
  right: 40px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 100;

  .nav-buttons {
    display: flex;
    flex-direction: column;
    gap: 16px;

    .nav-btn {
      width: 48px;
      height: 48px;
      border-radius: 24px;
      background: #fff;
      border: none;
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
      transition: all 0.3s ease;
      
      .el-icon {
        font-size: 20px;
        color: #666;
        transition: all 0.3s ease;
      }
      
      span {
        position: absolute;
        right: 100%;
        margin-right: 10px;
        padding: 6px 12px;
        background: rgba(0, 0, 0, 0.8);
        color: #fff;
        border-radius: 4px;
        font-size: 14px;
        white-space: nowrap;
        opacity: 0;
        visibility: hidden;
        transform: translateX(10px);
        transition: all 0.3s ease;
      }
      
      &:hover {
        width: 52px;
        height: 52px;
        background: $primary-color;
        
        .el-icon {
          color: #fff;
        }
        
        span {
          opacity: 1;
          visibility: visible;
          transform: translateX(0);
        }
      }
      
      &.active {
        background: $primary-color;
        
        .el-icon {
          color: #fff;
        }
      }
      
      &.back-to-top {
        opacity: 0;
        visibility: hidden;
        transform: translateY(20px);
        
        &.show {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
        }
      }
    }
  }
}

// 移动端适配
@media screen and (max-width: 768px) {
  .floating-nav {
    right: 20px;
    
    .nav-buttons {
      gap: 12px;
      
      .nav-btn {
        width: 40px;
        height: 40px;
        
        &:hover {
          width: 40px;
          height: 40px;
        }
        
        span {
          display: none;
        }
      }
    }
  }
}

.meinnDM-style {
  .data-governance-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 24px;
    margin-top: 40px;
  }

  .feature-card {
    background: var(--el-bg-color);
    border-radius: 8px;
    padding: 24px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
    transition: transform 0.3s ease;

    &:hover {
      transform: translateY(-5px);
    }

    .card-header {
      display: flex;
      align-items: center;
      margin-bottom: 16px;
      gap: 12px;

      .el-icon {
        font-size: 24px;
        color: var(--el-color-primary);
      }

      h3 {
        margin: 0;
        font-size: 18px;
        color: var(--el-text-color-primary);
      }
    }

    .feature-list {
      list-style: none;
      padding: 0;
      margin: 0;

      li {
        position: relative;
        padding-left: 20px;
        margin-bottom: 12px;
        color: var(--el-text-color-regular);
        font-size: 14px;
        line-height: 1.6;

        &:before {
          content: "•";
          position: absolute;
          left: 0;
          color: var(--el-color-primary);
        }
      }
    }
  }
}

.title-bar.meinnDM {
  background: linear-gradient(90deg, #409EFF 0%, #79bbff 100%);
}

.title-bar.idcb {
  background: linear-gradient(90deg, #67C23A 0%, #95D475 100%);
}

.monitor-dashboard {
  margin: 40px 0;
  
  .dashboard-main {
    .dashboard-preview {
      background: #1a1a1a;
      border-radius: 8px;
      overflow: hidden;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
      
      .preview-header {
        background: #2c2c2c;
        padding: 8px 16px;
        display: flex;
        gap: 8px;
        
        .dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: #ff5f56;
          
          &:nth-child(2) {
            background: #ffbd2e;
          }
          
          &:nth-child(3) {
            background: #27c93f;
          }
        }
      }
      
      .preview-content {
        padding: 20px;
        
        .grid-layout {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 20px;
          padding: 20px;
          
          .grid-item {
            background: #2c2c2c;
            border-radius: 6px;
            padding: 16px;
            display: flex;
            flex-direction: column;
            
            h5 {
              color: #fff;
              margin: 0 0 12px;
              font-size: 16px;
            }
            
            .chart-placeholder {
              flex: 1;
              width: 100%;
              height: 160px;
              overflow: hidden;
              border-radius: 4px;
              display: flex;
              align-items: center;
              justify-content: center;
              position: relative;
              user-select: none;
              -webkit-user-select: none;
              -moz-user-select: none;
              -ms-user-select: none;
              
              img {
                width: 100%;
                height: 100%;
                object-fit: cover;
                object-position: center;
                pointer-events: none;
                -webkit-user-drag: none;
                -khtml-user-drag: none;
                -moz-user-drag: none;
                -o-user-drag: none;
                user-drag: none;
              }
            }
          }
        }
      }
    }
  }
}

// 添加图片保护样式
.chart-placeholder::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.1);
  pointer-events: none;
  z-index: 1;
}

.training-system {
  background: linear-gradient(135deg, #e6f0ff 0%, #f0f7ff 100%);
  border-radius: 12px;
  padding: 40px;
  margin: 40px 0;
  
  .system-title {
    color: #1e40af;
    text-align: center;
    font-size: 24px;
    margin-bottom: 40px;
  }
  
  .training-cards {
    display: flex;
    justify-content: space-around;
    gap: 30px;
    
    .training-card {
      flex: 1;
      background: #fff;
      border-radius: 12px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
      overflow: hidden;
      transition: all 0.3s ease;
      
      &:hover {
        transform: translateY(-5px);
        box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
      }
      
      .card-header {
        background: #1e40af;
        color: #fff;
        padding: 12px;
        text-align: center;
        font-size: 18px;
      }
      
      .card-content {
        padding: 24px;
        min-height: 200px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: space-between;
        
        .avatar-box, .exam-paper, .certificate-box {
          width: 100px;
          height: 100px;
          background: #f1f5f9;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 20px;
          
          i {
            font-size: 40px;
            color: #1e40af;
          }
        }
        
        .exam-paper {
          position: relative;
          background: #fff;
          border: 2px solid #e2e8f0;
          
          .paper-lines {
            width: 80%;
            height: 60%;
            margin: 0 auto;
            background: repeating-linear-gradient(
              #fff,
              #fff 10px,
              #e2e8f0 10px,
              #e2e8f0 11px
            );
          }
        }
        
        .card-footer {
          color: #64748b;
          font-size: 16px;
          text-align: center;
          margin-top: auto;
        }
      }
    }
  }
}

@media screen and (max-width: 768px) {
  .grid-layout {
    grid-template-columns: 1fr;
    gap: 15px;
    padding: 15px;
    
    .grid-item {
      .chart-placeholder {
        height: 140px;
      }
    }
  }
}
</style> 